/**
 * Device fingerprinting utilities for account sharing detection.
 * All functions are wrapped in try-catch to ensure login works even if fingerprinting fails.
 */

const DEVICE_ID_KEY = '_did'

/**
 * Generate a UUID v4 with fallback for browsers without crypto.randomUUID().
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get or create a persistent device ID stored in localStorage.
 * Returns empty string if localStorage is unavailable or any error occurs.
 */
export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY)
    if (!id) {
      id = generateUUID()
      localStorage.setItem(DEVICE_ID_KEY, id)
    }
    return id
  } catch {
    return ''
  }
}

/**
 * Get a simple browser fingerprint based on screen size, timezone, language, and platform.
 * Returns empty string if any error occurs.
 */
export function getFingerprint(): string {
  try {
    const data = {
      sw: screen.width,
      sh: screen.height,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lang: navigator.language,
      plat: navigator.platform,
    }
    return JSON.stringify(data)
  } catch {
    return ''
  }
}
